'use client'

import { useIsDesktop } from '@/hooks/use-screen'
import { motion } from 'framer-motion'
import { useSearchParams } from 'next/navigation'
import { useState, useMemo } from 'react'
import { MdSort } from 'react-icons/md'

import { Pagination } from '@/components/atoms/pagination'
import { Checkbox } from '@/components/atoms/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/ui/popover'
import { ArticleCard } from '@/components/molecules/cards/article-card'
import Container from '@/components/templates/container/container'

import { getImageArticle } from '@/lib/utils/get-image-article'

const ITEMS_PER_PAGE = 4

const filterArticles = (articles: Article['items'], categories: string[]) => {
  if (categories?.length === 0) return articles

  return articles?.filter((article) => categories?.includes(article.fields.category))
}

const sortArticlesByDate = (articles: Article['items']) => {
  return articles.sort((a, b) => new Date(b.sys.updatedAt).getTime() - new Date(a.sys.updatedAt).getTime())
}

interface ArticleLatestProps {
  data: Article
  categoryList?: string[]
}

export const ArticleLatest = ({ data, categoryList }: ArticleLatestProps) => {
  const params = useSearchParams()
  const isDesktop = useIsDesktop()
  const [activePage, setActivePage] = useState(0)
  const categoryParam = params.get('category')
  const [selectedCategories, setSelectedCategories] = useState<string[]>(categoryParam ? [categoryParam] : [])

  const handleCategoryChange = (category: string) => {
    setSelectedCategories((prev) =>
      prev?.includes(category) ? prev?.filter((cat) => cat !== category) : [...prev, category]
    )
  }

  const sortedArticles = useMemo(() => sortArticlesByDate(data.items), [data.items])
  const filteredArticles = useMemo(
    () => filterArticles(sortedArticles, selectedCategories),
    [sortedArticles, selectedCategories]
  )

  const startIndex = activePage * ITEMS_PER_PAGE
  const visibleArticles = filteredArticles?.slice(startIndex, startIndex + ITEMS_PER_PAGE)
  const totalPages = Math.ceil(filteredArticles.length / ITEMS_PER_PAGE)

  const getArticleImageUrl = (item: Article['items'][0]) => {
    const imageId = item.fields.img?.sys?.id
    const imageUrl = imageId ? getImageArticle(data, imageId)?.fields?.file?.url : undefined

    return imageUrl ? `https:${imageUrl}` : '/static/images/dummy-article.png'
  }

  return (
    <section id="article-latest" className="py-8">
      <Container className="space-y-6 lg:space-y-10">
        <header className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
          <h2 className="text-xl font-bold lg:text-2xl text-secondary-blue">Artikel Terbaru</h2>
          <Popover>
            <PopoverTrigger className="flex items-center gap-1 font-semibold gtm-article-tag">
              <MdSort size={24} />
              Filter Kategori
            </PopoverTrigger>
            <PopoverContent align={isDesktop ? 'end' : 'start'} className="px-5 py-4 mt-3">
              <ul className="text-sm font-medium">
                {categoryList?.map((item) => (
                  <li key={item} className="flex items-center gap-2 py-3 border-b border-gray-100 gtm-article-tag">
                    <Checkbox
                      id={item.toLowerCase().replace(' ', '-')}
                      className="w-5 h-5"
                      checked={selectedCategories.includes(item)}
                      onCheckedChange={() => handleCategoryChange(item)}
                    />
                    <label className="cursor-pointer" htmlFor={item.toLowerCase().replace(' ', '-')}>
                      {item}
                    </label>
                  </li>
                ))}
              </ul>
            </PopoverContent>
          </Popover>
        </header>

        <motion.article
          key={activePage}
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {visibleArticles?.map((item, index) => (
            <motion.div
              key={item.fields.uid}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2, delay: index * 0.1 }}
            >
              <ArticleCard
                title={item.fields.title}
                category={item.fields.category}
                image={getArticleImageUrl(item)}
                uid={item.fields.uid ?? '#!'}
              />
            </motion.div>
          ))}
        </motion.article>

        {visibleArticles.length === 0 && <p className="w-full py-10 text-center">Artikel tidak ditemukan...</p>}

        <div className="flex justify-center">
          <Pagination totalPage={totalPages} activeIndex={activePage} onChange={setActivePage} />
        </div>
      </Container>
    </section>
  )
}
